/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { importProvidersFrom } from '@angular/core';
import { MatNativeDateModule } from '@angular/material/core';
import { Route, Routes } from '@angular/router';
import { CanShareClusterGuard } from '@app/cluster-management/page-cluster-management/guards/can-share-cluster.guard';
import { ContextActionClusterIdGuard } from '@app/cluster-management/page-cluster-management/guards/context-action-cluster-id.guard';
import { dataEncryptionDialogModelResolver } from '@app/cluster-management/services/data-encryption.resolver';
import { DIALOG_WIDTH_M } from '@app/common/constants/dialog-width';
import { DialogRoute } from '@app/common/modules/gmc-dialog';
import { clusterCanPerformLogsDownload } from '@app/core/guards/cluster-can-perform-logs-download.guard';
import { clusterCanUpdateLicenseGuard } from '@app/core/guards/cluster-can-update-license.guard';
import { HostedTargetGuard } from '@app/core/guards/hosted-target.guard';
import { isContextActionDataEncryption } from '@app/core/guards/is-context-action-data-encryption.guard';
import { only2xClustersGuard } from '@app/core/guards/only-2x-clusters.guard';
import { Dialog } from './constants';
import { dialogCcConnectionSettingsGuard } from './dialog-cc-connection-settings/dialog-cc-connection-settings.guard';
import { DIALOG_UPDATE_CLUSTER_LICENSE_ID } from './dialog-update-cluster-license/constants';
import { DIALOG_EDIT_ACCESS_LIST_WIDTH } from './edit-access-list/constants';

const credentials: Route = {
  path: 'credentials',
  loadComponent: () => import('./dialog-manage-cluster-users/dialog-manage-cluster-users.component'),
  data: {
    dialogConfig: { width: DIALOG_WIDTH_M, id: Dialog.MANAGE_CLUSTER_USERS_ID, autoFocus: false },
  },
  children: [
    {
      path: 'add',
      loadComponent: () => import('./dialog-add-cluster-user/dialog-add-cluster-user.component'),
      data: {
        dialogConfig: { width: DIALOG_WIDTH_M, id: Dialog.ADD_CLUSTER_USER_ID, autoFocus: false },
        navigateSettings: { commands: ['..'] },
      },
    } as DialogRoute,
    {
      path: ':userId',
      children: [
        {
          path: 'change-password',
          loadComponent: () =>
            import('./dialog-change-cluster-user-password/dialog-change-cluster-user-password.component'),
          data: {
            dialogConfig: { width: DIALOG_WIDTH_M, id: Dialog.CHANGE_CLUSTER_USER_PASSWORD_ID, autoFocus: false },
          },
        } as DialogRoute,
      ],
    },
  ],
};

const rename = {
  path: 'rename',
  loadComponent: () => import('./dialog-rename-cluster/dialog-rename-cluster.component'),
  data: {
    dialogConfig: { width: DIALOG_WIDTH_M, id: Dialog.RENAME_CLUSTER_ID },
  },
  canActivate: [only2xClustersGuard('contextActionClusterId')],
} as DialogRoute;

const destroy = {
  path: 'destroy',
  canLoad: [HostedTargetGuard],
  loadComponent: () => import('./dialog-destroy-nebula-cluster/dialog-destroy-nebula-cluster.component'),
  data: {
    dialogConfig: { width: DIALOG_WIDTH_M, id: Dialog.DESTROY_NEBULA_CLUSTER_ID },
  },
} as DialogRoute;

const share = {
  path: 'share',
  canActivate: [CanShareClusterGuard],
  loadComponent: () => import('./dialog-share-cluster/dialog-share-cluster.component'),
  data: {
    dialogConfig: { width: DIALOG_WIDTH_M, autoFocus: false, id: 'DIALOG_SHARE_CLUSTER_ID' },
  },
} as DialogRoute;

const accessList = {
  path: 'access-list',
  loadComponent: () => import('./dialog-edit-access-list/dialog-edit-access-list.component'),
  data: {
    dialogConfig: {
      width: DIALOG_EDIT_ACCESS_LIST_WIDTH,
      id: Dialog.EDIT_ACCESS_LIST_ID,
    },
  },
} as DialogRoute;

const dataEncryption: Route = {
  path: 'data-encryption',
  canActivate: [isContextActionDataEncryption],
  resolve: { dataEncryptionDialogModel: dataEncryptionDialogModelResolver },
  children: [
    {
      path: '',
      redirectTo: 'view',
      pathMatch: 'full',
    },
    {
      path: 'view',
      loadComponent: () => import('./dialog-data-encryption/dialog-data-encryption.component'),
      data: {
        dialogConfig: {
          width: DIALOG_WIDTH_M,
          autoFocus: false,
        },
        navigateSettings: { commands: ['../../..'] },
      },
    } as DialogRoute,
    {
      path: 'edit',
      loadComponent: () => import('./dialog-edit-data-encryption/dialog-edit-data-encryption.component'),
      data: {
        dialogConfig: {
          width: DIALOG_WIDTH_M,
          autoFocus: false,
        },
        navigateSettings: { commands: ['../../..'] },
      },
    } as DialogRoute,
  ],
};

// TODO CC-6833: Replace with DialogRoute
const downloadLogs: Route = {
  path: 'download-logs',
  canActivate: [clusterCanPerformLogsDownload],
  loadComponent: () => import('./dialog-logs/dialog-logs.component').then((m) => m.DialogLogsComponent),
  providers: [importProvidersFrom(MatNativeDateModule)],
  data: {
    dialogConfig: {
      width: DIALOG_WIDTH_M,
      autoFocus: false,
    },
    navigateSettings: { commands: ['../../..'] },
  },
};

const updateLicense = {
  path: 'update-license',
  canActivate: [clusterCanUpdateLicenseGuard],
  loadComponent: () => import('./dialog-update-cluster-license/dialog-update-cluster-license.component'),
  data: {
    dialogConfig: {
      width: DIALOG_WIDTH_M,
      id: DIALOG_UPDATE_CLUSTER_LICENSE_ID,
    },
    navigateSettings: { commands: ['../../..'] },
  },
} as DialogRoute;

const ccConnectionSettings = {
  path: 'cc-connection-settings',
  canActivate: [dialogCcConnectionSettingsGuard],
  loadComponent: () => import('./dialog-cc-connection-settings/dialog-cc-connection-settings.component'),
  data: {
    dialogConfig: {
      width: DIALOG_WIDTH_M,
      autoFocus: false,
    },
  },
} as DialogRoute;

export const clusterContextActionRoutes: Routes = [
  {
    path: ':contextActionClusterId',
    canActivate: [ContextActionClusterIdGuard],
    data: { navigateSettings: { commands: ['../..'] } },
    children: [
      share,
      destroy,
      accessList,
      rename,
      credentials,
      dataEncryption,
      downloadLogs,
      updateLicense,
      ccConnectionSettings,
    ],
  },
];
